import React, { useContext, useRef, useState } from "react";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import Checkbox from "antd/lib/checkbox";
import message from "antd/lib/message";
import Radio from "antd/lib/radio";
import Form, { RuleObject } from "antd/lib/form";
import { useMutation } from "react-query";
import { createApiClient } from "apiClient";
import { AppButton } from "components/button";
import { ButtonType } from "components/button/appButton";
import {
  PatientContext,
  PatientContextState
} from "contextApis/patientContext";
import { UserContext, UserContextState } from "contextApis/userContext";
import { SurveyInput } from "pages/symptoms/surveyForms/surveyInput";
import { Platform, UserRoles } from "constants/roles";
import { FormSubmitButton } from "pages/symptoms/surveyForms/FormSubmitButton";
import { FormClearButton } from "pages/symptoms/surveyForms/FormClearButton";
import {
  SurveyPage,
  SurveyContent,
  SurveyHeader,
  SurveyNoticeCard,
  SurveyQuestionCard,
  AnswerGrid,
  AnswerList,
  AnswerOption,
  SurveyFooter,
  SurveyProgressBar,
  FooterActions,
  FormItemStyle
} from "pages/symptoms/surveyForms/surveyFormComponents";

interface UlcersFormProps {
  setShowSurveyForm(show: boolean): void;
  selectedItems: string[];
  goToNextFormOrReturn(): void;
}

export const UlcersForm: React.FC<UlcersFormProps> = ({
  setShowSurveyForm,
  selectedItems,
  goToNextFormOrReturn
}) => {
  const params = new URLSearchParams(window.location.search);
  const platform = params.get("platform");
  const [currentPage, setCurrentPage] = useState(1);
  const [otherTempInput, setOtherTempInput] = useState("");
  const otherInputRef = useRef("");
  const [showSubmit, setShowSubmit] = useState(false);

  const [form] = Form.useForm();

  const userContext = useContext<UserContextState>(UserContext);
  const patientContext = useContext<PatientContextState>(PatientContext);

  const validateWhereIsTheUlcer = (
    rules: RuleObject,
    value: string,
    callback: (error?: string) => void
  ) => {
    if (
      otherInputRef.current === "" &&
      localStorage.getItem("whereIsTheUlcer")?.includes("Other")
    ) {
      callback("Please enter value for 'Other' field.");
    } else {
      callback();
    }
  };

  const userRole = sessionStorage.getItem("userRole");

  const { isLoading: isLoadingUlcersMutation, mutate: submitUlcersForm } =
    useMutation<string>(
      "add-bill",
      async () => {
        return await createApiClient().addUlcersFormData({
          patient_internal_id:
            userRole === UserRoles.PATIENT && userContext.userData
              ? userContext.userData?.internal_id
              : patientContext?.patientData?.id || "",
          ulcers: localStorage.getItem("hasUlcers") || "",
          location: localStorage.getItem("whereIsTheUlcer") || "",
          size: localStorage.getItem("sizeOfLargestUlcer") || "",
          appearance: localStorage.getItem("appearanceOfUlcer") || "",
          submitted_by: `${userContext.userData?.firstName} ${userContext.userData?.lastName}`
        });
      },
      {
        onSuccess: () => {
          message.success("Form Submitted Successfully");
          goToNextFormOrReturn();
        },
        onError: () => {
          message.error({
            content: (
              <span>
                There was a problem submitting this form, resubmit or{" "}
                <a onClick={() => goToNextFormOrReturn()}>
                  click here to continue to the next survey
                </a>
              </span>
            ),
            style: {
              bottom: "10px",
              position: "fixed",
              left: "30%"
            },
            duration: 5
          });
        }
      }
    );

  const onFinish = () => {
    //Values to be passed on to API from session storage
    if (localStorage.getItem("hasUlcers") === "No") {
      //Clear form and session values if no longer pertinent.
      form.resetFields([
        "Level of pain",
        "Where is the ulcer",
        "Size of largest ulcer",
        "Appearance of ulcer"
      ]);
      localStorage.setItem("levelOfPain", "");
      localStorage.setItem("whereIsTheUlcer", "");
      localStorage.setItem("sizeOfLargestUlcer", "");
      localStorage.setItem("appearanceOfUlcer", "");
      submitUlcersForm();
    } else if (currentPage === 1) {
      setCurrentPage(2);
    } else {
      if (localStorage.getItem("whereIsTheUlcer")?.includes("Other")) {
        //To handle 'other' input from Where is the ulcer section
        const finalwhereIsTheUlcer =
          localStorage.getItem("whereIsTheUlcer") + "," + otherInputRef.current;
        localStorage.setItem("whereIsTheUlcer", finalwhereIsTheUlcer);
      }
      submitUlcersForm();
    }
  };

  const onClear = () => {
    if (currentPage === 1) {
      form.resetFields(["Any ulcers on skin"]);
    } else {
      form.resetFields([
        "Level of pain",
        "Where is the ulcer",
        "Size of largest ulcer",
        "Appearance of ulcer"
      ]);
      otherInputRef.current === "";
      setOtherTempInput("");
    }
  };

  return (
    <SurveyPage>
      <div
        style={{
          marginBottom: "15px",
          display: platform === Platform.MOBILE ? "none" : "block"
        }}
      >
        <AppButton
          type={ButtonType.Primary}
          onClick={() => setShowSurveyForm(false)}
          buttonContent={
            <>
              <ArrowLeftOutlined /> Back
            </>
          }
        />
      </div>

      <SurveyContent>
        <Form
          name="ulcersForm"
          form={form}
          onFinish={onFinish}
          scrollToFirstError
          style={{ width: "100%" }}
        >
          <SurveyHeader title={`Survey - ${selectedItems[0]}`} />

          {currentPage === 1 && (
            <>
              <SurveyNoticeCard />

              <SurveyQuestionCard
                title="Have you noticed any ulcers on your skin?"
                required
              >
                <FormItemStyle
                  name="Any ulcers on skin"
                  label=""
                  rules={[{ required: true }]}
                  initialValue=""
                >
                  <Radio.Group
                    value={localStorage.getItem("hasUlcers")}
                    onChange={e => {
                      if (e.target.value === "No") {
                        setShowSubmit(true);
                      } else {
                        setShowSubmit(false);
                      }
                      localStorage.setItem("hasUlcers", e.target.value);
                    }}
                    style={{ width: "100%" }}
                  >
                    <AnswerGrid>
                      <AnswerOption>
                        <Radio value="Yes">Yes</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="No">No</Radio>
                      </AnswerOption>
                    </AnswerGrid>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>
            </>
          )}

          {currentPage === 2 && (
            <>
              <SurveyQuestionCard title="Where is the ulcer?" required>
                <FormItemStyle
                  name="Where is the ulcer"
                  label=""
                  rules={[
                    { required: true },
                    {
                      validator: validateWhereIsTheUlcer,
                      validateTrigger: "submit"
                    }
                  ]}
                  initialValue=""
                >
                  <Checkbox.Group
                    style={{ width: "100%" }}
                    onChange={e =>
                      localStorage.setItem("whereIsTheUlcer", e.toString())
                    }
                    value={sessionStorage
                      .getItem("whereIsTheUlcer")
                      ?.split(/,/)}
                  >
                    <AnswerList>
                      <AnswerOption>
                        <Checkbox value="Legs/Feet">Legs/Feet</Checkbox>
                      </AnswerOption>
                      <AnswerOption>
                        <Checkbox value="Buttocks / Back">
                          Buttocks / Back
                        </Checkbox>
                      </AnswerOption>
                      <AnswerOption>
                        <Checkbox value="Abdomen">Abdomen</Checkbox>
                      </AnswerOption>
                      <AnswerOption>
                        <Checkbox value="Arms">Arms</Checkbox>
                      </AnswerOption>
                      <AnswerOption
                        style={{
                          flexDirection: "column",
                          alignItems: "flex-start",
                          padding: "16px 20px"
                        }}
                      >
                        <Checkbox value="Other">Other</Checkbox>
                        <SurveyInput
                          value={otherTempInput ?? otherInputRef}
                          placeholder=""
                          onChange={e => {
                            setOtherTempInput(e.target.value);
                            otherInputRef.current = e.target.value;
                          }}
                          style={{ width: "100%", marginTop: "8px" }}
                        />
                      </AnswerOption>
                    </AnswerList>
                  </Checkbox.Group>
                </FormItemStyle>
              </SurveyQuestionCard>

              <SurveyQuestionCard
                title="What is size of the largest ulcer?"
                required
              >
                <FormItemStyle
                  name="Size of largest ulcer"
                  label=""
                  rules={[{ required: true }]}
                  initialValue=""
                >
                  <Radio.Group
                    value={localStorage.getItem("sizeOfLargestUlcer")}
                    onChange={e =>
                      localStorage.setItem("sizeOfLargestUlcer", e.target.value)
                    }
                    style={{ width: "100%" }}
                  >
                    <AnswerList>
                      <AnswerOption>
                        <Radio value="Small (less than 2 cm)">
                          Small (less than 2 cm)
                        </Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Medium (Between 2 cm and 5 cm)">
                          Medium (Between 2 cm and 5 cm)
                        </Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Big (more than 5 cm)">
                          Big (more than 5 cm)
                        </Radio>
                      </AnswerOption>
                    </AnswerList>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>

              <SurveyQuestionCard
                title="What is the appearance of the ulcer?"
                required
              >
                <FormItemStyle
                  name="Appearance of ulcer"
                  label=""
                  rules={[{ required: true }]}
                  initialValue=""
                >
                  <Radio.Group
                    value={localStorage.getItem("appearanceOfUlcer")}
                    onChange={e =>
                      localStorage.setItem("appearanceOfUlcer", e.target.value)
                    }
                    style={{ width: "100%" }}
                  >
                    <AnswerList>
                      <AnswerOption>
                        <Radio value="Dry">Dry</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Moist">Moist</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Draining">Draining</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="With an offensive odor">
                          With an offensive odor
                        </Radio>
                      </AnswerOption>
                    </AnswerList>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>
            </>
          )}

          <SurveyFooter>
            <SurveyProgressBar currentPage={currentPage} totalPages={2} />

            <FooterActions>
              <Form.Item>
                <FormClearButton onClear={onClear} />
              </Form.Item>

              {currentPage !== 1 && (
                <AppButton
                  type={ButtonType.Primary}
                  buttonContent={
                    <>
                      <ArrowLeftOutlined /> Back
                    </>
                  }
                  onClick={() => setCurrentPage(1)}
                  style={{ width: "105px" }}
                />
              )}

              <Form.Item>
                <FormSubmitButton
                  type={ButtonType.Primary}
                  buttonContent={
                    currentPage !== 1 || showSubmit ? (
                      "Submit"
                    ) : (
                      <>
                        Next <ArrowRightOutlined />
                      </>
                    )
                  }
                  htmlType="submit"
                  loading={isLoadingUlcersMutation}
                />
              </Form.Item>
            </FooterActions>
          </SurveyFooter>
        </Form>
      </SurveyContent>
    </SurveyPage>
  );
};
